import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { OrderWithFlavorService } from '../services/orderwithflavor.service';
import { Shift, VendorCategory } from '../models/flavor.model';
import { Restaurant } from '../models/restaurant.model';
import { map, tap, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class OrderWithFlavorStore {
  private _flavorShifts$ = new BehaviorSubject<Shift[]>([]);
  private _restaurants$ = new BehaviorSubject<Restaurant[]>([]);
  private _shift = new BehaviorSubject<Shift>(null);
  private _vendorCategories$ = new BehaviorSubject<VendorCategory[]>([]);
  private _groupedFlavorShifts$ = new BehaviorSubject<Shift[]>([]);
  private _isLoadingMore$ = new BehaviorSubject<boolean>(false);
  private loadedDateRanges: { start: string; end: string }[] = [];

  public flavorShifts$: Observable<Shift[]> = this._flavorShifts$.asObservable();
  public restaurants$: Observable<Restaurant[]> = this._restaurants$.asObservable();
  public vendorCategories$: Observable<VendorCategory[]> = this._vendorCategories$.asObservable();
  public groupedFlavorShifts$: Observable<Shift[]> = this._groupedFlavorShifts$.asObservable();
  public isLoadingMore$: Observable<boolean> = this._isLoadingMore$.asObservable();

  constructor(private orderWithFlavorService: OrderWithFlavorService) {}

  get shift(): Shift {
    return this._shift.getValue();
  }

  set shift(value: Shift) {
    this._shift.next(value);
  }

  get currentRestaurants(): Restaurant[] {
    return this._restaurants$.getValue();
  }

  set restaurants(value: Restaurant[]) {
    this._restaurants$.next(value);
  }

  public getVendorCategories(): VendorCategory[] {
    return this._vendorCategories$.value;
  }
  

  public loadShifts(vanityLink: string, restaurants: Restaurant[], startDate: Date, endDate: Date, locationId?:string, shiftId?: string): void {
    this.orderWithFlavorService.getShifts(vanityLink, startDate, endDate, locationId, shiftId).subscribe(
      (shifts) => {
        // If a shiftId was requested but returned no match, fall back to the
        // full date-range query (single-restaurant flow expects upcoming shifts
        // even when the deep-linked shift no longer exists).
        if (shiftId && (!shifts || shifts.length === 0)) {
          this.orderWithFlavorService.getShifts(vanityLink, startDate, endDate, locationId).subscribe(
            (fallbackShifts) => {
              this._flavorShifts$.next(fallbackShifts);
              this.updateRestaurantsWithLocations(fallbackShifts, restaurants);
              this.trackLoadedRange(startDate, endDate);
            }
          );
          return;
        }
        this._flavorShifts$.next(shifts);
        this.updateRestaurantsWithLocations(shifts, restaurants);
        this.trackLoadedRange(startDate, endDate);
      }
    );
  }

  public loadShiftsProgressive(vanityLink: string, restaurants: Restaurant[], startDate: Date, endDate: Date, unsubscribe$?: Observable<void>): Observable<{isInitial: boolean}> {
    // Calculate day 1 end date (end of the start date)
    const day1End = new Date(startDate);
    day1End.setHours(23, 59, 59, 999);

    // Check if we already have this data loaded
    if (this.hasDataForRange(startDate, endDate) && this._vendorCategories$.value.length > 0) {
      console.log('Data already loaded for this range');
      return new Observable(subscriber => {
        subscriber.next({isInitial: true});
        subscriber.complete();
      });
    }

    // Load day 1 first
    return this.orderWithFlavorService.getShifts(vanityLink, startDate, day1End).pipe(
      tap((shifts) => {
        console.log('Day 1 loaded:', shifts.length, 'shifts');
        this._flavorShifts$.next(shifts);
        this.updateRestaurantsWithLocations(shifts, restaurants);
        this.trackLoadedRange(startDate, day1End);
      }),
      map(() => {
        // Check if we need to load more days
        const needsMoreDays = day1End < endDate;

        if (needsMoreDays) {
          // Load remaining days in the background
          this._isLoadingMore$.next(true);
          const day2Start = new Date(day1End.getTime() + 1);

          // Build the observable chain for background loading
          let backgroundLoad$ = this.orderWithFlavorService.getShifts(vanityLink, day2Start, endDate);

          // Apply takeUntil if unsubscribe signal is provided
          if (unsubscribe$) {
            backgroundLoad$ = backgroundLoad$.pipe(takeUntil(unsubscribe$));
          }

          backgroundLoad$.subscribe(
            (remainingShifts) => {
              console.log('Remaining days loaded:', remainingShifts.length, 'shifts');
              // Merge with existing shifts, skipping overnight shifts already loaded by the day-1 query
              const currentShifts = this._flavorShifts$.getValue();
              const existingShiftIds = new Set(currentShifts.map(shift => shift.id));
              const newShifts = remainingShifts.filter(shift => !existingShiftIds.has(shift.id));
              const allShifts = [...currentShifts, ...newShifts];
              this._flavorShifts$.next(allShifts);
              this.updateRestaurantsWithLocations(allShifts, restaurants);
              this.trackLoadedRange(day2Start, endDate);
              this._isLoadingMore$.next(false);
            },
            (error) => {
              console.error('Error loading remaining days:', error);
              this._isLoadingMore$.next(false);
            },
            () => {
              // Complete handler - also set loading to false in case of early unsubscribe
              console.log('Background loading completed or cancelled');
              this._isLoadingMore$.next(false);
            }
          );
        }

        return {isInitial: true};
      })
    );
  }

  private trackLoadedRange(startDate: Date, endDate: Date): void {
    const startStr = this.formatDate(startDate);
    const endStr = this.formatDate(endDate);

    // Check if this range overlaps with existing ranges
    const overlaps = this.loadedDateRanges.some(range =>
      (startStr >= range.start && startStr <= range.end) ||
      (endStr >= range.start && endStr <= range.end) ||
      (startStr <= range.start && endStr >= range.end)
    );

    if (!overlaps) {
      this.loadedDateRanges.push({ start: startStr, end: endStr });
    }
  }

  private hasDataForRange(startDate: Date, endDate: Date): boolean {
    const startStr = this.formatDate(startDate);
    const endStr = this.formatDate(endDate);

    return this.loadedDateRanges.some(range =>
      range.start <= startStr && range.end >= endStr
    );
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  public clearLoadedRanges(): void {
    this.loadedDateRanges = [];
  }

  private updateRestaurantsWithLocations(flavorShifts: Shift[], restaurants: Restaurant[]): void {
    let updatedRestaurants = [];
    for (const shift of flavorShifts) {
      const restaurant = restaurants.find((r) => r.locationId === shift.kyoo_location_id);
      if (restaurant) {
        let nextRestaurant = { ...restaurant, address: { ...restaurant.address }};
        nextRestaurant.address.addressLine = shift.location_id.street_number_and_name;
        if(nextRestaurant.address.coordinates){
          nextRestaurant.address.coordinates.latitude = parseFloat(shift.location_id.area.latitude);
          nextRestaurant.address.coordinates.longitude = parseFloat(shift.location_id.area.longitude);
        }
        // Update sellerDeliveryLocations with delivery location names
        if (shift.location_id.delivery_enabled && shift.location_id.delivery_locations && shift.location_id.delivery_locations.length > 0) {
          const deliveryLocationNames = shift.location_id.delivery_locations.map(dl => dl.name);
          if (!nextRestaurant.sellerDeliveryLocations) {
            nextRestaurant.sellerDeliveryLocations = [];
          }
          for (const name of deliveryLocationNames) {
            if (!nextRestaurant.sellerDeliveryLocations.includes(name)) {
              nextRestaurant.sellerDeliveryLocations.push(name);
            }
          }
        }
        if (!updatedRestaurants.find((r) => r.name === nextRestaurant.name)) {
          updatedRestaurants.push(nextRestaurant);
        }
      }
    }
    this._restaurants$.next(updatedRestaurants);
    this.updateVendorCategories();
  }

  private updateVendorCategories(): void {
    const shifts = this._flavorShifts$.getValue();
    const restaurants = this._restaurants$.getValue();
    const locationMap = new Map<string, { restaurants: Restaurant[], shifts: Shift[], childLocationOrder: number }>();

    for (const shift of shifts) {
        const location = shift.location_id;
        if (location && location.name) {
            const kyooLocationId = shift.kyoo_location_id;
            if (!locationMap.has(location.name)) {
                locationMap.set(location.name, {
                    restaurants: [],
                    shifts: [],
                    childLocationOrder: location.child_location_order
                });
            }

            const locationData = locationMap.get(location.name);
            const correspondingRestaurant = restaurants.find(r => r.locationId === kyooLocationId);

            if (correspondingRestaurant) {

                const existingRestaurants = locationData.restaurants;
                if (!existingRestaurants.some(r => r.locationId === correspondingRestaurant.locationId)) {
                    existingRestaurants.push(correspondingRestaurant);
                }

                locationData.shifts.push(shift);
            }
        }
    }

    const vendorCategories = Array.from(locationMap, ([locationName, { restaurants, shifts, childLocationOrder }]) => ({
        locationName,
        restaurants,
        shifts,
        childLocationOrder
    }))
    .sort((a, b) => {
        // If both have childLocationOrder, sort ascending
        if (a.childLocationOrder != null && b.childLocationOrder != null) {
            return a.childLocationOrder - b.childLocationOrder;
        }
        // If a is null, put it last
        if (a.childLocationOrder == null && b.childLocationOrder != null) {
            return 1;
        }
        // If b is null, put it last
        if (a.childLocationOrder != null && b.childLocationOrder == null) {
            return -1;
        }
        // If both are null, maintain current order
        return 0;
    });
    // console.log({vendorCategories})
    this._vendorCategories$.next(vendorCategories);
  }

  public getRestaurantById(id: string, address: string): Observable<Restaurant> {
    return this.restaurants$.pipe(
      map(restaurants => restaurants.find(item => item.locationId === id && item.address.addressLine === address))
    );
  }

  public processShifts(vanityLink: string, restaurants: Restaurant[], startDate: Date, endDate: Date): Observable<Restaurant[]> {
    return this.orderWithFlavorService.getShifts(vanityLink, startDate, endDate).pipe(
      map((shifts: Shift[]) => {
        // console.log({shifts,restaurants})
        this._flavorShifts$.next(shifts);
        this.updateRestaurantsWithLocations(shifts, restaurants);
        return this._restaurants$.getValue();
      })
    );
  }

  public filterShiftsByLocationIds(locationIds: string[]): void {
    console.log("filterShiftsByLocationIds")
    
    // Defensive check: ensure locationIds is a valid array
    if (!locationIds || !Array.isArray(locationIds) || locationIds.length === 0) {
      console.warn('filterShiftsByLocationIds: locationIds is not a valid array', locationIds);
      this._groupedFlavorShifts$.next([]);
      return;
    }
    
    const allShifts = this._flavorShifts$.getValue();
    
    // Defensive check: ensure allShifts is a valid array
    if (!allShifts || !Array.isArray(allShifts)) {
      console.warn('filterShiftsByLocationIds: allShifts is not a valid array', allShifts);
      this._groupedFlavorShifts$.next([]);
      return;
    }
    
    // Step 1: Group shifts by day
    const shiftsByDay: Record<string, Shift[]> = {};
    for (const shift of allShifts) {
      if (!shiftsByDay[shift.day]) shiftsByDay[shift.day] = [];
      shiftsByDay[shift.day].push(shift);
    }
    
    const result: Shift[] = [];
  
    // Step 2: Check each day's shifts
    for (const [day, dayShifts] of Object.entries(shiftsByDay)) {
      const timezone = dayShifts[0].location_id.area.timezone;
  
      // Step 3: Group shifts by locationId for that day
      const shiftsByLocation: Record<string, Shift[]> = {};
      for (const locId of locationIds) {
        const locShifts = dayShifts.filter(s => s.kyoo_location_id === locId);
        if (locShifts.length > 0) {
          shiftsByLocation[locId] = locShifts;
        }
      }
      // console.log({shiftsByLocation})
      
        for (const locId of locationIds) {
          const shifts = shiftsByLocation[locId];
          // Defensive check: ensure shifts is a valid array
          if (shifts && Array.isArray(shifts)) {
            for (const shift of shifts) {
              if (!result.includes(shift)) {
                result.push(shift);
              }
            }
          }
        }
    }
  
    this._groupedFlavorShifts$.next(result);
  }
  
  
  
  // Utility function to get all combinations of one shift per location
  private cartesianProduct<T>(arrays: T[][]): T[][] {
    return arrays.reduce<T[][]>(
      (acc, curr) =>
        acc.flatMap(a => curr.map(b => [...a, b])),
      [[]]
    );
  }
  
  public clearFlavorStore():void {
    this._restaurants$.next([]);
    this._flavorShifts$.next([]);
    this._groupedFlavorShifts$.next([]);
    this._vendorCategories$.next([]);
    this.loadedDateRanges = [];
    this._isLoadingMore$.next(false);
  }
  
}
